/**
 * Lieferschein-Positionen in Kontor lesen und korrigieren (02.10.2026).
 *
 * Die Routen delegieren an ScanFlow (`kontor_material.py`): dort liegen
 * Matching, Gedächtnis je Lieferant und der Eintrag ins Projekt. Nach einer
 * Änderung ziehen die Projektsicht (Liste, „geliefert …"), die Materialliste
 * des Lieferanten und „Neue Artikel prüfen" nach.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost } from "@vimaana/ui";

/** Eine Position wie `scanflow.LieferscheinPositionOut`. */
export type LieferscheinPosition = {
  id: string;
  beleg_id: string;
  position_nr: number;
  katalog_id: string;
  katalog_name: string;
  /** So steht es auf dem Lieferschein. */
  freitext_name: string;
  menge: number;
  einheit: string;
  /** leer/vorschlag = offen · gedaechtnis/katalog/mensch = sicher · ignoriert = keine Ware. */
  zuordnung: string;
};

export type LieferscheinPositionPatch = { katalog_id?: string; menge?: number; einheit?: string };

const enc = encodeURIComponent;
const schluessel = (belegId: string) => ["lieferschein-positionen", belegId];

export function useLieferscheinPositionen(belegId: string, aktiv: boolean) {
  return useQuery<LieferscheinPosition[]>({
    queryKey: schluessel(belegId),
    queryFn: () => apiGet<LieferscheinPosition[]>(`/api/kontor/belege/${enc(belegId)}/positionen`),
    enabled: aktiv && !!belegId,
  });
}

function useNachziehen(belegId: string, projektId: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: schluessel(belegId) });
    qc.invalidateQueries({ queryKey: ["projekt", projektId, "belege"] });
    qc.invalidateQueries({ queryKey: ["projekt-material", projektId] });
    qc.invalidateQueries({ queryKey: ["lieferant"] });
    qc.invalidateQueries({ queryKey: ["artikel"] });
  };
}

/** Eine Position von Hand hinzufügen bzw. entfernen (02.10.2026). */
export function useLieferscheinPositionNeuWeg(belegId: string, projektId: string) {
  const nachziehen = useNachziehen(belegId, projektId);
  const neu = useMutation({
    mutationFn: (v: { katalog_id?: string; freitext_name?: string; menge?: number; einheit?: string }) =>
      apiPost<LieferscheinPosition>(`/api/kontor/belege/${enc(belegId)}/positionen`, v),
    onSuccess: nachziehen,
  });
  const weg = useMutation({
    mutationFn: (id: string) => apiDelete<{ geloescht: string }>(`/api/kontor/positionen/${enc(id)}`),
    onSuccess: nachziehen,
  });
  return { neu, weg };
}

export function useLieferscheinPositionAendern(belegId: string, projektId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string } & LieferscheinPositionPatch) => {
      const { id, ...patch } = v;
      return apiPatch<LieferscheinPosition>(`/api/kontor/positionen/${enc(id)}`, patch);
    },
    onSuccess: (neu) => {
      qc.setQueryData<LieferscheinPosition[]>(schluessel(belegId), (alt) =>
        (alt ?? []).map((p) => (p.id === neu.id ? neu : p)));
      qc.invalidateQueries({ queryKey: schluessel(belegId) });
      qc.invalidateQueries({ queryKey: ["projekt", projektId, "belege"] });
      qc.invalidateQueries({ queryKey: ["projekt-material", projektId] });
      qc.invalidateQueries({ queryKey: ["lieferant"] });
      qc.invalidateQueries({ queryKey: ["artikel"] });
    },
  });
}
